import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Key, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const HMACSecretSetup = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Security Configuration
        </CardTitle>
        <CardDescription>
          Set up HMAC authentication for secure webhook communication
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Key className="h-4 w-4" />
          <AlertDescription>
            <strong>Required:</strong> Add <code className="bg-muted px-1 rounded">ELEVENLABS_CANDIDATE_LOOKUP_SECRET</code> and <code className="bg-muted px-1 rounded">ELEVENLABS_SCREENING_WEBHOOK_SECRET</code> to your Supabase Edge Function secrets.
          </AlertDescription>
        </Alert>

        <div className="space-y-3 text-sm">
          <div className="space-y-2">
            <h4 className="font-medium">1. Generate Webhook Secrets</h4>
            <p className="text-muted-foreground">
              Create two secure random strings (32+ characters each) for your webhook secrets.
            </p>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">For Candidate Lookup webhook:</p>
              <code className="block p-2 bg-muted rounded text-xs">
                openssl rand -hex 32
              </code>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">For Call Completion webhook:</p>
              <code className="block p-2 bg-muted rounded text-xs">
                openssl rand -hex 32
              </code>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">2. Add to Supabase Secrets</h4>
            <p className="text-muted-foreground">
              Go to your Supabase project settings → Edge Functions → Secrets and add both:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 text-muted-foreground">
              <li>Name: <code className="bg-muted px-1 rounded">ELEVENLABS_CANDIDATE_LOOKUP_SECRET</code></li>
              <li>Value: Your first generated webhook secret</li>
              <li>Name: <code className="bg-muted px-1 rounded">ELEVENLABS_SCREENING_WEBHOOK_SECRET</code></li>
              <li>Value: Your second generated webhook secret</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">3. Configure ElevenLabs Webhooks</h4>
            <p className="text-muted-foreground">
              Set up two webhooks in your ElevenLabs agent configuration:
            </p>
            
            <div className="space-y-3 pl-4">
              <div className="space-y-1">
                <h5 className="font-medium text-xs">A. Conversation Initiation Webhook (Pre-call personalization)</h5>
                <p className="text-xs text-muted-foreground">URL:</p>
                <code className="block p-2 bg-muted rounded text-xs">
                  https://bdvwkygtrettvwiwbxbc.supabase.co/functions/v1/candidate-lookup
                </code>
                <ul className="list-disc list-inside ml-4 space-y-1 text-xs text-muted-foreground">
                  <li>Enable "Fetch initiation client data from webhook"</li>
                  <li>Set authentication to HMAC-SHA256</li>
                  <li>Use <code className="bg-muted px-1 rounded">ELEVENLABS_CANDIDATE_LOOKUP_SECRET</code></li>
                </ul>
              </div>

              <div className="space-y-1">
                <h5 className="font-medium text-xs">B. Call Completion Webhook (Post-call analysis)</h5>
                <p className="text-xs text-muted-foreground">URL:</p>
                <code className="block p-2 bg-muted rounded text-xs">
                  https://bdvwkygtrettvwiwbxbc.supabase.co/functions/v1/elevenlabs-screening-webhook
                </code>
                <ul className="list-disc list-inside ml-4 space-y-1 text-xs text-muted-foreground">
                  <li>Set as conversation completion webhook</li>
                  <li>Set authentication to HMAC-SHA256</li>
                  <li>Use <code className="bg-muted px-1 rounded">ELEVENLABS_SCREENING_WEBHOOK_SECRET</code></li>
                </ul>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-2">
              Both webhooks require <code className="bg-muted px-1 rounded">x-signature</code> and <code className="bg-muted px-1 rounded">x-timestamp</code> headers.
            </p>
          </div>
        </div>

        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Security Note:</strong> Keep your webhook secret secure and never share it publicly. 
            If compromised, regenerate it and update both Supabase and ElevenLabs configurations.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};